import GoogleMapReact from 'google-map-react';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { deliveryTypeStore } from '../../store/deliveryTypeStore';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { DELIVERY_TYPE } from 'src/constants/deliveryTypes';
import { DEBOUNCE_TIME } from 'src/constants/main';
import { defaultCoordinates, defaultGoogleMapOptions, defaultZoom, googleMapBootstrapURLKeys } from 'src/constants/map';
import { branchStore } from 'src/mobx/branchStore';
import { modalStore } from 'src/mobx/modalStore';
import { ICoordinates } from 'src/types/location';
import { debounce } from 'src/utils/perfomance';
import PinIndicator from 'src/view/components/Controls/PinIndicator/PinIndicator';
import MapZoom from 'src/view/components/Map/MapZoom/MapZoom';
import './style.scss';

const DeliveryTypeMap: React.FC = observer((): JSX.Element => {
  const [mapCenterPosition, setMapCenterPosition] = useState<ICoordinates>(modalStore.deliveryType?.location || defaultCoordinates);
  const [mapZoom, setMapZoom] = useState<number>(defaultZoom);

  useEffect(() => {
    if (deliveryTypeStore.deliveryType === DELIVERY_TYPE.DELIVERY) {
      mapCenterPositionChanged();
    }
  }, [mapCenterPosition.lat, mapCenterPosition.lng]);

  useEffect(() => {
    const { location, branch } = deliveryTypeStore.selectedDeliveryState[DELIVERY_TYPE.DELIVERY];
    if (!location || !branch) {
      mapCenterPositionChanged();
    }
  }, [deliveryTypeStore.deliveryType]);

  const onMapCenterPositionWasChanged = useCallback(() => {
    deliveryTypeStore.getDeliveryDependencies(mapCenterPosition.lat, mapCenterPosition.lng);
  }, [mapCenterPosition]);
  const mapCenterPositionChanged = debounce(onMapCenterPositionWasChanged, DEBOUNCE_TIME.MEDIUM);

  const onDragEnd = useCallback(({ center }: { center: { lat: () => number; lng: () => number } }) => {
    setMapCenterPosition({ lat: center.lat(), lng: center.lng() });
  }, []);

  const zoomChangeHandler = useCallback(
    (increase: boolean) => () => {
      let zoom = increase ? mapZoom + 1 : mapZoom - 1;
      if (zoom > 16) zoom = 16;
      if (zoom < 2) zoom = 2;
      setMapZoom(zoom);
    },
    [mapZoom]
  );

  const mapCenter = useMemo(() => {
    const branch = deliveryTypeStore.selectedDeliveryState[deliveryTypeStore.deliveryType].branch;
    const location = deliveryTypeStore.selectedDeliveryState[DELIVERY_TYPE.DELIVERY].location;

    if (deliveryTypeStore.deliveryType === DELIVERY_TYPE.DELIVERY && location?.lat && location?.lng) {
      return {
        lat: location.lat,
        lng: location.lng,
      };
    }

    if ((deliveryTypeStore.deliveryType === DELIVERY_TYPE.PICKUP || deliveryTypeStore.deliveryType === DELIVERY_TYPE.DINE_IN) && branch) {
      return {
        lat: branch.latitude,
        lng: branch.longitude,
      };
    }

    return defaultCoordinates;
  }, [deliveryTypeStore.selectedDeliveryState, deliveryTypeStore.deliveryType]);

  return (
    <div className="delivery-map bg-bluelight">
      <GoogleMapReact
        zoom={mapZoom}
        bootstrapURLKeys={googleMapBootstrapURLKeys()}
        center={mapCenter}
        yesIWantToUseGoogleMapApiInternals
        onDragEnd={onDragEnd}
        options={defaultGoogleMapOptions}
      >
        {/* branches pin indicators */}
        {branchStore.pagination.data.map((branch) => (
          <PinIndicator key={branch.id} name={Icons.Pin} size={30} lat={branch.latitude} lng={branch.longitude} className="color-mainblue" />
        ))}
      </GoogleMapReact>
      {/* zoom change buttons */}
      <MapZoom onZoomIn={zoomChangeHandler(true)} onZoomOut={zoomChangeHandler(false)} />
      {/* center pin indicator */}
      {deliveryTypeStore.deliveryType === DELIVERY_TYPE.DELIVERY ? (
        <PinIndicator name={Icons.MapLocation} size={50} className="map-center-icon color-mainblue" />
      ) : null}
    </div>
  );
});

export default DeliveryTypeMap;
